import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiPlus,
  FiSearch,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  permissionCodes: [],
};

export default function RolesPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [rolesData, permissionsData] = await Promise.all([
        apiRequest("/roles", { accessToken }),
        apiRequest("/roles/permissions", { accessToken }),
      ]);

      setRoles(rolesData || []);
      setPermissions(permissionsData || []);
    } catch (err) {
      setError(err.message || "Unable to load roles.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadData();
  }, [isAuthLoading, accessToken, loadData]);

  const filteredRoles = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return roles;

    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(term) ||
        role.code.toLowerCase().includes(term),
    );
  }, [roles, search]);

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Administration / Roles</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Roles & Permissions
          </h1>

          <p className="mt-2 text-gray-500">
            Create roles and define what each user group can access.
          </p>
        </div>

        <button
          onClick={() => setCreateOpen(true)}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
        >
          <FiPlus />
          Create Role
        </button>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-3">
        <Stat label="Total Roles" value={roles.length} />
        <Stat
          label="System Roles"
          value={roles.filter((role) => role.isSystem).length}
          color="text-blue-600"
        />
        <Stat
          label="Custom Roles"
          value={roles.filter((role) => !role.isSystem).length}
          color="text-primary-700"
        />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative w-full md:w-96">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search roles..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>
        </div>

        {error && (
          <div className="m-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                {["Role", "Type", "Permissions", "Users"].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading roles…
                  </td>
                </tr>
              ) : filteredRoles.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No roles found.
                  </td>
                </tr>
              ) : (
                filteredRoles.map((role) => (
                  <tr key={role.id} className="border-b border-gray-100">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                          <FiShield />
                        </div>

                        <div>
                          <p className="font-semibold text-gray-900">
                            {role.name}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {role.code}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          role.isSystem
                            ? "bg-blue-100 text-blue-700"
                            : "bg-primary-50 text-primary-700"
                        }`}
                      >
                        {role.isSystem ? "System" : "Custom"}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {role.permissions?.length || 0} permissions
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FiUsers className="text-gray-400" />
                        {role._count?.users || 0}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateRoleModal
        open={createOpen}
        permissions={permissions}
        onClose={() => setCreateOpen(false)}
        onCreated={loadData}
      />
    </div>
  );
}

function CreateRoleModal({ open, onClose, permissions, onCreated }) {
  const { accessToken } = useAuth();

  const [form, setForm] = useState(emptyForm);
  const [openModules, setOpenModules] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const groupedPermissions = useMemo(() => {
    return permissions.reduce((groups, permission) => {
      if (!groups[permission.module]) {
        groups[permission.module] = [];
      }

      groups[permission.module].push(permission);
      return groups;
    }, {});
  }, [permissions]);

  if (!open) return null;

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const togglePermission = (code) => {
    setForm((previous) => ({
      ...previous,
      permissionCodes: previous.permissionCodes.includes(code)
        ? previous.permissionCodes.filter(
            (permissionCode) => permissionCode !== code,
          )
        : [...previous.permissionCodes, code],
    }));
  };

  const toggleModule = (module) => {
    const moduleCodes = groupedPermissions[module].map(
      (permission) => permission.code,
    );

    const allSelected = moduleCodes.every((code) =>
      form.permissionCodes.includes(code),
    );

    setForm((previous) => ({
      ...previous,
      permissionCodes: allSelected
        ? previous.permissionCodes.filter((code) => !moduleCodes.includes(code))
        : [...new Set([...previous.permissionCodes, ...moduleCodes])],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.code.trim()) {
      setError("Role name and role code are required.");
      return;
    }

    if (form.permissionCodes.length === 0) {
      setError("Select at least one permission.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await apiRequest("/roles", {
        method: "POST",
        accessToken,
        body: {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          permissionCodes: form.permissionCodes,
        },
      });

      await onCreated();
      onClose();

      setForm(emptyForm);
      setOpenModules({});
    } catch (err) {
      setError(err.message || "Unable to create role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold">Create Role</h2>

            <p className="mt-1 text-sm text-gray-500">
              Select exactly what this role can access.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[70vh] overflow-y-auto p-6">
            {error && (
              <div className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              <Input
                label="Role Name *"
                value={form.name}
                onChange={(value) => updateForm("name", value)}
                placeholder="Store Manager"
              />

              <Input
                label="Role Code *"
                value={form.code}
                onChange={(value) =>
                  updateForm(
                    "code",
                    value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
                  )
                }
                placeholder="STORE_MANAGER"
              />

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium">
                  Description
                </label>

                <textarea
                  rows="2"
                  value={form.description}
                  onChange={(event) =>
                    updateForm("description", event.target.value)
                  }
                  placeholder="Can manage a retail store and its inventory."
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700"
                />
              </div>
            </div>

            <div className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Permissions *</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {form.permissionCodes.length} permission
                    {form.permissionCodes.length === 1 ? "" : "s"} selected
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(groupedPermissions).map(
                  ([module, modulePermissions]) => {
                    const allSelected = modulePermissions.every((permission) =>
                      form.permissionCodes.includes(permission.code),
                    );

                    const isOpen = openModules[module];

                    return (
                      <div
                        key={module}
                        className="overflow-hidden rounded-2xl border border-gray-200"
                      >
                        <div className="flex items-center justify-between p-4">
                          <label className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => toggleModule(module)}
                              className="accent-primary-700"
                            />

                            <span className="font-semibold capitalize">
                              {module.replace(/_/g, " ")}
                            </span>

                            <span className="text-xs text-gray-500">
                              ({modulePermissions.length})
                            </span>
                          </label>

                          <button
                            type="button"
                            onClick={() =>
                              setOpenModules((previous) => ({
                                ...previous,
                                [module]: !previous[module],
                              }))
                            }
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                          >
                            {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                          </button>
                        </div>

                        {isOpen && (
                          <div className="grid gap-3 border-t border-gray-100 bg-gray-50 p-4 sm:grid-cols-2">
                            {modulePermissions.map((permission) => (
                              <label
                                key={permission.code}
                                className="flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3"
                              >
                                <input
                                  type="checkbox"
                                  checked={form.permissionCodes.includes(
                                    permission.code,
                                  )}
                                  onChange={() =>
                                    togglePermission(permission.code)
                                  }
                                  className="mt-1 accent-primary-700"
                                />

                                <span>
                                  <span className="block text-sm font-medium">
                                    {permission.action.replace(/_/g, " ")}
                                  </span>

                                  <span className="mt-1 block text-xs text-gray-500">
                                    {permission.code}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-gray-200 px-5"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
            >
              <FiCheck />
              {saving ? "Creating…" : "Create Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      />
    </div>
  );
}

function Stat({ label, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
